import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { StatusDot } from "@/components/StatusPill";
import { cn } from "@/lib/utils";

/** Searchable machine picker (label, customer, serial, internal no). */
export const MachineCombobox = ({ machines = [], value, onChange, placeholder = "Vel maskin", testid = "machine-combobox", disabled }) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => machines.find((m) => m.id === value), [machines, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className={cn("h-12 w-full justify-between px-3 font-normal", !selected && "text-muted-foreground")}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Wrench className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">
              {selected ? (
                <>
                  <span className="font-semibold text-foreground">{selected.label}</span>
                  {selected.customer_name && <span className="text-muted-foreground"> · {selected.customer_name}</span>}
                </>
              ) : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command
          filter={(val, search) => (val.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
        >
          <CommandInput placeholder="Søk maskin, kunde, serienr …" data-testid={`${testid}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>Ingen maskiner funne.</CommandEmpty>
            <CommandGroup>
              {machines.map((m) => (
                <CommandItem
                  key={m.id}
                  value={[m.label, m.customer_name, m.serial_number, m.internal_number, m.brand, m.type].filter(Boolean).join(" ")}
                  onSelect={() => {
                    onChange(m.id);
                    setOpen(false);
                  }}
                  data-testid={`${testid}-option-${m.id}`}
                  className="cursor-pointer gap-3 py-2.5"
                >
                  <StatusDot status={m.status?.overall_status} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{m.label}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[m.customer_name, m.serial_number && `SN ${m.serial_number}`].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Check className={cn("h-4 w-4", value === m.id ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
